import { apiErrorMessage } from '@/api/client';
import { documentsApi } from '@/api/documents.api';
import { useDetectionPolling } from '@/hooks/useDetectionPolling';
import type { DetectionStatusModel } from '@/types/documents.types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** `starting` — the POST is out; `polling` — the job runs; `reloading` — the review is being re-read. */
type RetryPhase = 'idle' | 'starting' | 'polling' | 'reloading';

export interface RetrySkippedPagesOptions {
  /** `finished_at` of the run whose pages are retried, read when `start` is called. */
  lastFinishedAt: string | null | undefined;
  /** Reloads the review after a job that did run; awaited before the button is released. */
  onDone: () => Promise<void>;
}

export interface RetrySkippedPages {
  /** True from the click until the job has ended and, after a finished one, the review is reloaded. */
  isRetrying: boolean;
  /** The retry could not be started or did not run; cleared by the next attempt and by a reload. */
  error: string | null;
  start: () => Promise<void>;
}

/**
 * A `done` job that still carries the retried run's `finished_at` is that run put back: the
 * backend restores the previous finished job when a retry fails or is cancelled, so failure never
 * arrives as `failed`. Comparing against the old run rather than the click keeps clock skew out.
 */
const isRestoredRun = (status: DetectionStatusModel, lastFinishedAt: string | null | undefined): boolean =>
  Boolean(status.finished_at && lastFinishedAt) &&
  Date.parse(status.finished_at as string) <= Date.parse(lastFinishedAt as string);

/**
 * "Znovu analyzovat přeskočené stránky": `POST /documents/{id}/detection/retry`, then the same
 * polling as the upload route until the job ends. A job that ran to `done` reloads the review
 * through `onDone` (fresh findings, fresh `skipped_pages`); anything else — the old run restored,
 * a `failed` job, a vanished session — leaves the review as it was and reports why.
 * Provisional — built against the contract while the backend endpoint is in progress.
 */
export const useRetrySkippedPages = (
  documentId: string,
  { lastFinishedAt, onDone }: RetrySkippedPagesOptions
): RetrySkippedPages => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<RetryPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const retriedRunFinishedAt = useRef<string | null | undefined>(undefined);
  const polling = useDetectionPolling(documentId, phase === 'polling');

  // Polling stopped: reload after a job that ran, report everything else, and release the button.
  useEffect(() => {
    if (phase !== 'polling' || polling.isPolling) return;
    const status = polling.status;
    if (status?.status === 'done' && !isRestoredRun(status, retriedRunFinishedAt.current)) {
      setError(null);
      setPhase('reloading');
      onDone().finally(() => setPhase('idle'));
      return;
    }
    setError(polling.isExpired ? t('common.sessionExpired') : (status?.error ?? t('review.skippedPages.retryFailed')));
    setPhase('idle');
  }, [phase, polling.isPolling, polling.status, polling.isExpired, onDone, t]);

  const start = useCallback(async () => {
    retriedRunFinishedAt.current = lastFinishedAt;
    setPhase('starting');
    setError(null);
    try {
      await documentsApi.retrySkippedPages(documentId);
      setPhase('polling');
    } catch (err) {
      setError(apiErrorMessage(err, t('review.skippedPages.retryFailed')));
      setPhase('idle');
    }
  }, [documentId, lastFinishedAt, t]);

  return { isRetrying: phase !== 'idle', error, start };
};
